/**
 * The ring this device placed, while nobody has answered it (plan #71).
 *
 * The caller goes into the room as soon as the ring starts (the app since
 * plan #71, the web since 2026-10-02 with webOutgoingRing), so the room is
 * where the ring is cancelled: the waiting pane's "Cancel call", the 60 s
 * expiry (the portal's group ring bar: CALLER_RING_TIMEOUT_MS in
 * client-profile.js), and a hang-up before anyone answered. All three send p2p_cancel, which closes the
 * call with zero duration and no CDR (vrs-server ws/p2p-handlers.js
 * handleP2PCancel); call_end would bill a connected duration. p2p_cancel ends
 * the whole call, so it is never sent once anyone has answered: from then on
 * the hang-up and the host's sheet are unchanged.
 *
 * The server answers with call_ended and p2p_call_cancelled for everyone,
 * the caller included; the queue middleware closes the room on the first and
 * ignores the echo for a room this device already left.
 *
 * Held in module state rather than redux or a component: the toolbar and the
 * waiting pane come and go, and the exit path in the middleware reads it.
 */
import { queueService } from './InterpreterQueueService';
/** The ring stops itself after this long, as on the web portal. */
export const OUTGOING_RING_TIMEOUT_MS = 60000;
/** How long "Cancelling…" waits for the server before saying so. */
export const OUTGOING_RING_CANCEL_CONFIRM_MS = 10000;
export const CANCEL_CALL_LABEL = 'Cancel call';
export const CANCELLING_CALL_STATUS = 'Cancelling…';
export const CANCEL_UNCONFIRMED_MESSAGE = 'Could not confirm cancellation. Try again.';
export const CANCEL_FAILED_MESSAGE = 'Could not cancel. Try again.';
export const NO_ANSWER_MESSAGE = 'No one answered.';
/**
 * Error codes p2p_cancel is refused with (getP2PControlContext). They carry no
 * `operation`, so they are told apart only while a cancel is on its way.
 */
const CANCEL_REFUSAL_CODES = new Set([
    'AUTH_REQUIRED', 'CALL_ENDED', 'FORBIDDEN', 'INTERNAL_ERROR', 'NOT_FOUND', 'VALIDATION_ERROR'
]);
/** Refusals that mean the call is already over: nothing is left to cancel. */
const CALL_OVER_CODES = new Set(['CALL_ENDED', 'NOT_FOUND']);
let ring = null;
let view = null;
let expiryTimer;
let confirmTimer;
let handlers;
let uninstall;
// Calls whose ring is settled: a replayed p2p_ringing (reconnect delivery)
// must not bring an answered or cancelled ring back.
const settledCallIds = [];
const subscribers = new Set();
function publish() {
    view = ring ? { callId: ring.callId, phase: ring.phase, ...ring.problem ? { problem: ring.problem } : {} } : null;
    subscribers.forEach(listener => listener());
}
function clearTimer(timer) {
    if (timer !== undefined) {
        clearTimeout(timer);
    }
}
function settle(callId) {
    if (!settledCallIds.includes(callId)) {
        settledCallIds.push(callId);
        if (settledCallIds.length > 32) {
            settledCallIds.shift();
        }
    }
}
/** Drop the ring for this call (answered, over, or taken by the exit). */
function forget(callId) {
    if (!ring || (callId && ring.callId !== callId)) {
        return;
    }
    settle(ring.callId);
    clearTimer(expiryTimer);
    clearTimer(confirmTimer);
    expiryTimer = undefined;
    confirmTimer = undefined;
    ring = null;
    publish();
}
function update(patch) {
    if (ring) {
        ring = { ...ring, ...patch };
        publish();
    }
}
/** Subscribe to changes (useSyncExternalStore). */
export function subscribeOutgoingRing(listener) {
    subscribers.add(listener);
    return () => {
        subscribers.delete(listener);
    };
}
/** The ring as the room shows it; the same object until it changes. */
export function getOutgoingRingView() {
    return view;
}
/** Whether this device placed the call it is in and nobody has answered yet. */
export function hasUnansweredOutgoingRing() {
    return Boolean(ring && handlers?.activeCallId() === ring.callId);
}
/**
 * Ring for this call now, until it is answered, over or timeoutMs runs out.
 *
 * @param {string} callId - The call.
 * @param {string|undefined} calleeId - Whom it rings (a direct call).
 * @param {boolean} groupCall - Whether it is a group call.
 * @param {number} timeoutMs - How long it may ring.
 * @returns {void}
 */
function startRing(callId, calleeId, groupCall, timeoutMs) {
    forget();
    ring = {
        callId,
        calleeId: calleeId || undefined,
        groupCall,
        phase: 'ringing'
    };
    expiryTimer = setTimeout(() => {
        expiryTimer = undefined;
        if (ring?.callId === callId && ring.phase === 'ringing') {
            handlers?.onExpired(callId);
        }
    }, timeoutMs);
    publish();
}
/**
 * The ring started (p2p_ringing, sent only to the device that placed it).
 * Someone else's call, or a replay of a settled one, is ignored.
 */
export function noteOutgoingRing(data) {
    const me = handlers?.localUserId();
    if (!data.callId || !me || data.clientId !== me || settledCallIds.includes(data.callId)
        || ring?.callId === data.callId) {
        return;
    }
    startRing(data.callId, data.calleeId, data.groupCall === true, OUTGOING_RING_TIMEOUT_MS);
}
/**
 * The web caller's room page takes over the ring its portal placed: the
 * portal heard p2p_ringing, recorded when the ring started on the stored call
 * and opened the room (webOutgoingRing.ts). The ring keeps its own 60 s, so
 * a reload during the ring does not start it again.
 *
 * @param {Object} data - The call, its callee and when the ring started.
 * @param {number} now - The current time.
 * @returns {boolean} Whether the ring is this page's now.
 */
export function resumeOutgoingRing(data, now = Date.now()) {
    const left = OUTGOING_RING_TIMEOUT_MS - (now - data.startedAt);
    if (!handlers || !data.callId || settledCallIds.includes(data.callId) || ring?.callId === data.callId
        || !Number.isFinite(left) || left <= 0 || left > OUTGOING_RING_TIMEOUT_MS) {
        return false;
    }
    startRing(data.callId, data.calleeId, data.groupCall === true, left);
    return true;
}
/** Someone answered or joined: from now on the call is ended the usual way. */
export function noteOutgoingRingAnswered(callId) {
    if (callId) {
        forget(callId);
    }
}
/**
 * Cancel the ring from the room ("Cancel call"). Sent only on a live
 * socket; the server's call_ended then closes the room.
 *
 * @returns {boolean} Whether p2p_cancel went out.
 */
export function cancelOutgoingRing() {
    if (!ring || ring.phase === 'cancelling' || handlers?.activeCallId() !== ring.callId) {
        return false;
    }
    const { callId, calleeId } = ring;
    if (!queueService.cancelP2PCall(callId, calleeId)) {
        update({ problem: CANCEL_FAILED_MESSAGE });
        return false;
    }
    clearTimer(confirmTimer);
    update({ phase: 'cancelling', problem: undefined });
    confirmTimer = setTimeout(() => {
        confirmTimer = undefined;
        if (ring?.callId === callId && ring.phase === 'cancelling') {
            update({ phase: 'ringing', problem: CANCEL_UNCONFIRMED_MESSAGE });
        }
    }, OUTGOING_RING_CANCEL_CONFIRM_MS);
    return true;
}
/**
 * The room is being left. If this device placed the call and nobody answered,
 * the exit cancels the ring instead of ending a call.
 *
 * @param {string} [callId] - The call being left.
 * @returns {boolean} True when p2p_cancel went out, so the caller must not
 * send call_end; false to end the call as usual (someone answered, not this
 * device's ring, or no live socket for p2p_cancel).
 */
export function cancelOutgoingRingOnExit(callId) {
    if (!callId || !ring || ring.callId !== callId) {
        return false;
    }
    const { calleeId } = ring;
    forget(callId);
    // Sent again even when a Cancel is already on its way: a repeat is
    // answered as already cancelled, and a lost one would leave the callee
    // ringing. Offline, the exit falls back to call_end, which the socket
    // sends once it is back.
    return queueService.cancelP2PCall(callId, calleeId);
}
/**
 * Leave the ring on the call but stop its timer: a direct call was declined
 * or reported unanswered. Hanging up still cancels it.
 */
function stopRinging(callId) {
    if (ring && callId && ring.callId === callId && ring.phase === 'ringing') {
        clearTimer(expiryTimer);
        expiryTimer = undefined;
        update({ phase: 'stopped', problem: undefined });
    }
}
/**
 * A queue error while a cancel is on its way is that cancel's refusal.
 *
 * @returns {boolean} Whether the error was the cancel's (the caller then
 * shows nothing else for it).
 */
function onQueueError(data) {
    if (!ring || ring.phase !== 'cancelling' || data.operation || !data.code || !CANCEL_REFUSAL_CODES.has(data.code)) {
        return false;
    }
    const { callId } = ring;
    clearTimer(confirmTimer);
    confirmTimer = undefined;
    if (CALL_OVER_CODES.has(data.code)) {
        forget(callId);
        handlers?.onCallOver(callId);
    }
    else {
        update({ phase: 'ringing', problem: CANCEL_FAILED_MESSAGE });
    }
    return true;
}
/**
 * Whether the queue middleware should leave this error to the ring (a
 * refused p2p_cancel). Call it first in the shared error listener.
 */
export function isOutgoingRingCancelError(data) {
    return onQueueError(data);
}
/**
 * Follow the queue events that answer, stop or end this device's ring.
 * Installed once per sign-in (native) or room page (web) by the queue
 * middleware; the service drops every listener at sign-out, and
 * resetOutgoingRing clears what is left.
 */
export function installOutgoingRing(next) {
    uninstall?.();
    handlers = next;
    const onRinging = (data) => noteOutgoingRing(data);
    const onAnswered = (data) => {
        if (data.userId && data.userId === handlers?.localUserId()) {
            return;
        }
        noteOutgoingRingAnswered(data.callId);
    };
    const onDeclined = (data) => {
        // One decline in a group call changes nothing for the others' rings.
        if (ring && ring.callId === data.callId && !ring.groupCall) {
            stopRinging(data.callId);
        }
    };
    const onUnanswered = (data) => stopRinging(data.callId);
    // p2p_call_cancelled is a loose event: only its callId is read.
    const onOver = (data) => {
        const callId = data && typeof data === 'object' ? data.callId : undefined;
        if (typeof callId === 'string' && callId && ring?.callId === callId) {
            const wasHere = handlers?.activeCallId() === callId;
            forget(callId);
            if (wasHere) {
                handlers?.onCallOver(callId);
            }
        }
    };
    // A group member's own departure reaches only them; one naming someone
    // else is not the end of this call.
    const onEnded = (data) => {
        if (typeof data.participantId === 'string' && data.participantId
            && data.participantId !== handlers?.localUserId()) {
            return;
        }
        forget(data.callId);
    };
    queueService.on('p2pRinging', onRinging);
    queueService.on('p2pAccepted', onAnswered);
    queueService.on('p2pParticipantJoined', onAnswered);
    queueService.on('p2pDeclined', onDeclined);
    queueService.on('p2pTargetOffline', onUnanswered);
    queueService.on('p2pGroupNoAnswer', onUnanswered);
    queueService.on('p2pCallCancelled', onOver);
    queueService.on('callEnded', onEnded);
    uninstall = () => {
        queueService.off('p2pRinging', onRinging);
        queueService.off('p2pAccepted', onAnswered);
        queueService.off('p2pParticipantJoined', onAnswered);
        queueService.off('p2pDeclined', onDeclined);
        queueService.off('p2pTargetOffline', onUnanswered);
        queueService.off('p2pGroupNoAnswer', onUnanswered);
        queueService.off('p2pCallCancelled', onOver);
        queueService.off('callEnded', onEnded);
    };
}
/** Signed out: no ring, no timers, no listeners. */
export function resetOutgoingRing() {
    uninstall?.();
    uninstall = undefined;
    forget();
    handlers = undefined;
    settledCallIds.length = 0;
}
